import {
  Button,
  StandaloneLink,
  styled,
  Text,
} from '@zeiss/beyond-online-react';
import { useBreakpoint, useCopyToClipboard } from '@zeiss/lib-helpers';
import React from 'react';

import { EmptyState, IllustrationConfig } from 'src/components/EmptyState';
import { ButtonGroup } from 'src/snowflakes';
import { StyledContainer } from 'src/types';

const Container = styled.div<StyledContainer>`
  display: flex;
  flex-direction: column;
  gap: ${({ $isMinTablet }) =>
    /* istanbul ignore next -- @preserve */
    $isMinTablet ? 'var(--spacing-2xl)' : 'var(--spacing-l)'};
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ $isMinTablet }) =>
    /* istanbul ignore next -- @preserve */
    $isMinTablet ? 'var(--spacing-l)' : '0'};
`;
const DetailsContainer = styled.div<StyledContainer>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  align-items: center;
  justify-content: center;
  padding: ${({ $isMinTablet }) =>
    /* istanbul ignore next -- @preserve */
    $isMinTablet ? 'var(--spacing-s) var(--spacing-l)' : 'var(--spacing-s)'};
  margin-bottom: ${({ $isMinTablet }) =>
    /* istanbul ignore next -- @preserve */
    !$isMinTablet && 'var(--spacing-m)'};
  text-align: center;
  background-color: var(--color-global-background-01);
  border-radius: var(--border-radius-m);

  & a {
    white-space: nowrap;
  }

  & p {
    margin: 0;
  }
`;

const TransactionalContainer = styled.div<StyledContainer>`
  display: flex;
  flex-direction: ${({ $isMinDesktopL }) =>
    /* istanbul ignore next -- @preserve */
    $isMinDesktopL ? 'row' : 'column'};
  gap: var(--spacing-xs);
  align-items: center;
`;

interface ErrorWithTransactionId {
  transactionIdDetails: TransactionIdDetails;
}
interface ErrorWithoutTransactionId {
  transactionIdDetails?: never;
}

export type ErrorStateProps = {
  buttonText: string;
  description: string;
  headline: string;
  illustrationConfig: IllustrationConfig;
  onClick: () => void;
  showCta?: boolean;
} & (ErrorWithoutTransactionId | ErrorWithTransactionId);

interface TransactionIdDetails {
  copiedText: string;
  copyText: string;
  transactionIdText: string;
}

/**
 *
 * To prevent extra re-renders on clicking Copy button (link), the button and the copy functionality are extracted into a separate component.
 * This way, only the button re-renders when clicked, not the entire ErrorState component.
 */
const CopyButton = ({
  copiedText,
  copyText,
  transactionId,
}: {
  copiedText: string;
  copyText: string;
  transactionId: string;
}) => {
  const { copyToClipboard: copy, textCopied } = useCopyToClipboard();

  return (
    <StandaloneLink
      href=""
      icon={textCopied ? 'Checkmark' : 'CopyContent'}
      onClick={(e) => {
        e.preventDefault();
        copy(transactionId);
      }}
      rel="noopener noreferrer"
      size="s"
    >
      {textCopied ? copiedText : copyText}
    </StandaloneLink>
  );
};

export const ErrorStateComponent = ({
  buttonText,
  description,
  headline,
  illustrationConfig,
  onClick,
  showCta = true,
  transactionIdDetails,
}: ErrorStateProps) => {
  const isMinTablet = useBreakpoint('tablet');
  const isMinDesktopL = useBreakpoint('desktopL');
  const [transactionId, setTransactionId] = React.useState<string | null>(null);

  const { copiedText, copyText, transactionIdText } =
    transactionIdDetails ?? {};

  React.useEffect(() => {
    const storedTransactionId = localStorage.getItem('x-transactionid');
    if (storedTransactionId) {
      return setTransactionId(storedTransactionId);
    }

    const meta = document.querySelector('meta[name="sl-trace-id"]');
    if (meta) {
      const traceId = meta.getAttribute('content') || '';

      // Ignore the unfilled template placeholder from the static HTML.
      if (traceId && traceId !== '{{ .traceId }}') {
        return setTransactionId(traceId);
      }
    }
  }, []);

  return (
    <Container $isMinTablet={isMinTablet}>
      <EmptyState
        align="center"
        description={description}
        headline={headline}
        illustrationConfig={illustrationConfig}
        size={isMinTablet ? /* istanbul ignore next -- @preserve */ 'l' : 'm'}
      >
        {/* Without CTA the gap is bigger between the content and the transaction id as designed.
          TODO: Check latest Beyond version to see if this is fixed. */}
        {showCta ? (
          <ButtonGroup variant="form">
            <Button onClick={onClick}>{buttonText}</Button>
          </ButtonGroup>
        ) : null}
      </EmptyState>
      {transactionId && transactionIdDetails ? (
        <DetailsContainer $isMinTablet={isMinTablet}>
          <Text size="s">{transactionIdText}</Text>
          <TransactionalContainer $isMinDesktopL={isMinDesktopL}>
            <Text size="s">{transactionId}</Text>
            <CopyButton
              copiedText={copiedText!}
              copyText={copyText!}
              transactionId={transactionId}
            />
          </TransactionalContainer>
        </DetailsContainer>
      ) : null}
    </Container>
  );
};
